// The show strip on /deals (module 10 U149, U158; the brief DEALS-PAGE-BRIEF-2026-09-22.md section 4
// item 2; drawn in screens/2026-09-22-deals-concepts/concept-a-desk.html). Exported for the Deals desk,
// which is track `dealspage`'s page, to place under its head: this file owns what the strip reads and
// says, that page owns where it sits.
//
// Drawn only while a show is ahead or running with a meeting booked (U158). A strip that could not be
// read is not drawn either: the desk is about deals, and a failed side read must not put an error at
// the top of it; the show's own page says when its tracker could not be read. No blue on the strip:
// blue is where you are (the brief, section 4).
import { ActionButton } from '../parts/index.js';
import { useLoad } from '../records/shared.js';
import { showApi, type CurrentShow } from './api.js';
import { cityOfZone, companyOf, interestedOf, plateOf, showDates } from './words.js';
import './show.css';

export function ShowStrip({ store, open }: { store: string; open: (path: string) => void }) {
  const [loaded] = useLoad(() => showApi.current(store), [store]);
  if (loaded.kind !== 'ready' || loaded.data.shows.length === 0) return null;
  return <>{loaded.data.shows.map((s) => <Strip key={s.id} s={s} open={open} />)}</>;
}

function Strip({ s, open }: { s: CurrentShow; open: (path: string) => void }) {
  const next = s.readersNext;
  const title = s.city ? `${s.name}, ${s.city}` : s.name;
  return (
    <section className="sh-strip" aria-label={`The show: ${s.name}`}>
      <div className="sh-strip-what">
        <h2 className="sh-strip-name">{title}</h2>
        <div className="sh-meta">
          {s.stand ? <span>Stand {s.stand}</span> : null}
          <span>{showDates(s.startsOn, s.endsOn)}</span>
          <span>{s.teamMeetingCount === 0 ? 'No meetings booked yet' : `${s.teamMeetingCount} meeting${s.teamMeetingCount === 1 ? '' : 's'} booked by the team`}</span>
          {s.notBookedCount > 0 ? <span>{s.notBookedCount} target{s.notBookedCount === 1 ? '' : 's'} not booked</span> : null}
        </div>
      </div>
      <div className="sh-strip-next">
        {next && next.startsAt ? <span className="sh-meta">Your next meeting there, {cityOfZone(s.timeZone)} time</span> : null}
        {next && next.startsAt
          ? (
            <div className="sh-nx">
              <span className="sh-plate">{plateOf(next.startsAt, s.timeZone)}</span>
              <b>{companyOf(next)}</b>
              <span>{[next.contactName ? `with ${next.contactName}` : null, next.products.length ? `about ${interestedOf(next.products)}` : null].filter(Boolean).join(', ')}</span>
            </div>
          )
          : <div className="sh-nx"><span>You have no meetings at {s.name}. The team has {s.teamMeetingCount}.</span></div>}
      </div>
      <ActionButton reading type="button" className="sh-strip-go" onClick={() => open(`/deals/shows/${s.id}`)}>The agenda and targets</ActionButton>
    </section>
  );
}
